/**
 * Per-row confirmation for an invitee import.
 *
 * The previous panel showed three numbers — "N update existing", "N new guests",
 * "N would duplicate" — and listed only the duplicates. A host could never see
 * WHICH existing guest a row was about to overwrite, which is exactly the thing
 * that has to be checked on a roster holding "Alex", "Alex Greeley", "Mom",
 * "Dad" and "Mama".
 *
 * So every proposed match renders as `pasted row → existing guest`, with that
 * guest's email, phone, RSVP and who invited them: enough to tell one "Mom" from
 * another. Ambiguous rows offer the candidates and no default.
 *
 * The one bulk control covers exactly the rows the server flagged as guesses, and
 * it is gated on the host ticking a box that says what they are asserting. It is
 * never a control the host can hit on the way past — that is what it was, under a
 * label ("unambiguous") that described the opposite of what it did.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Divider, FormControlLabel, Paper, Stack, Typography,
} from '@mui/material';
import {
  acceptAllConfirmable,
  bulkAcceptLabel,
  bulkVouchSentence,
  confirmableRows,
  defaultDecisions,
  matchKeyLabel,
  summarizeDecisions,
} from '../../../lib/inviteeImport';
import type {
  ImportDecisions,
  InviteeImportMatchedGuest,
  InviteeImportPlanRow,
  InviteeImportPreview,
} from '../../../lib/inviteeImport';
import { plural, pluralWord } from '../../../lib/plural';

function GuestCard({ guest }: { guest: InviteeImportMatchedGuest }) {
  const details = [
    guest.email || 'no email',
    guest.phone || 'no phone',
    `RSVP ${guest.rsvpStatus}`,
    guest.invitedBy ? `invited by ${guest.invitedBy}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <Box>
      <Typography variant="body2" fontWeight={700}>{guest.fullName}</Typography>
      <Typography variant="caption" color="text.secondary">{details}</Typography>
    </Box>
  );
}

function PlanRow({
  row,
  decision,
  onDecide,
}: {
  row: InviteeImportPlanRow;
  decision: ImportDecisions[number] | undefined;
  onDecide: (rowIndex: number, next: ImportDecisions[number]) => void;
}) {
  const kind = (decision ?? { kind: 'pending' }).kind;
  const pasted = [row.fullName, row.email, row.phone].filter(Boolean).join(' · ');
  const accepted = kind === 'accept'
    ? (decision as { kind: 'accept'; inviteeId: string }).inviteeId
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, borderColor: kind === 'pending' ? 'warning.main' : undefined }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Typography variant="body2" fontWeight={700}>Row {row.rowIndex + 1}: {pasted}</Typography>
        <Chip
          size="small"
          color={kind === 'accept' ? 'info' : kind === 'create' ? 'success' : 'warning'}
          label={kind === 'accept' ? 'attach to guest' : kind === 'create' ? 'add as new guest' : 'needs your decision'}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {row.reason}
      </Typography>

      {row.matchedGuest ? (
        <Box sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {matchKeyLabel(row.matchKey) ? `matched on ${matchKeyLabel(row.matchKey)} →` : '→'}
          </Typography>
          <GuestCard guest={row.matchedGuest} />
        </Box>
      ) : null}

      {row.candidates.length > 0 ? (
        <Stack spacing={1} sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'warning.main' }}>
          {row.candidates.map((candidate) => (
            <Stack
              key={candidate.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
            >
              <GuestCard guest={candidate} />
              <Button
                size="small"
                variant={accepted === candidate.id ? 'contained' : 'outlined'}
                onClick={() => onDecide(row.rowIndex, { kind: 'accept', inviteeId: candidate.id })}
              >
                This one
              </Button>
            </Stack>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
        {row.matchedInviteeId ? (
          <Button
            size="small"
            variant={kind === 'accept' ? 'contained' : 'outlined'}
            onClick={() => onDecide(row.rowIndex, { kind: 'accept', inviteeId: row.matchedInviteeId as string })}
          >
            Yes, same person
          </Button>
        ) : null}
        <Button
          size="small"
          color="success"
          variant={kind === 'create' ? 'contained' : 'outlined'}
          onClick={() => onDecide(row.rowIndex, { kind: 'create' })}
        >
          Add as a new guest
        </Button>
      </Stack>
    </Paper>
  );
}

export default function InviteeImportPlanPanel({
  preview,
  decisions,
  onDecisions,
  saving,
  onCancel,
  onConfirm,
}: {
  preview: InviteeImportPreview;
  decisions: ImportDecisions;
  onDecisions: (next: ImportDecisions) => void;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const summary = summarizeDecisions(preview, decisions);
  /*
    The host has to assert, in as many words, that they are vouching for these
    matches before the bulk control will fire. State rather than a confirm dialog
    so the assertion sits on screen next to the rows it covers.
  */
  const [vouched, setVouched] = useState(false);
  /*
    Only the ones still waiting. Counting every acceptable row meant the button
    offered to accept matches it had already accepted — it read "Accept the 12
    matches" over a list where 11 were settled, and it never went away no
    matter how many times it was pressed.
  */
  const bulkRows = confirmableRows(preview).filter(
    (row) => (decisions[row.rowIndex] ?? { kind: 'pending' }).kind === 'pending',
  );

  const decide = (rowIndex: number, next: ImportDecisions[number]) => {
    onDecisions({ ...decisions, [rowIndex]: next });
  };

  /*
    Rows needing a decision first: on a 137-row paste the handful that need a
    human must not be buried under the hundred that do not.

    Ordered by the SERVER's plan, not by the host's decisions so far. Sorting on
    live decisions re-flowed the list on every click: settling the top row sent
    it to the bottom and pulled the next one up under a cursor that was already
    moving toward "Yes, same person". On this screen a misplaced click merges two
    different people and sends one guest's invitation to the other's address, so
    the list holds still while the host works down it.
  */
  const ordered = useMemo(() => {
    const initial = defaultDecisions(preview);
    const rank = (row: InviteeImportPlanRow) =>
      (initial[row.rowIndex] ?? { kind: 'pending' }).kind === 'pending' ? 0 : 1;
    return [...preview.rows].sort((a, b) => rank(a) - rank(b) || a.rowIndex - b.rowIndex);
  }, [preview]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1 }}>
        {plural(preview.rows.length, 'row')} pasted · roster {preview.rosterBefore} → {summary.rosterAfter}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" color="info" label={`${summary.updates} attach to existing`} />
        <Chip size="small" color="success" label={`${summary.creates} new ${pluralWord(summary.creates, 'guest')}`} />
        <Chip
          size="small"
          color={summary.pending > 0 ? 'warning' : 'default'}
          label={`${summary.pending} need your decision`}
        />
      </Stack>

      {/*
        A promise about someone's data has to be one the code keeps, and this
        text has now been wrong in both directions. It first said the name was
        kept when the old write overwrote it; it was then corrected to "including
        their name" at the same time as the write stopped touching full_name at
        all (see UpdateInviteeAsync — full_name, source and source_external_id are
        deliberately absent from the UPDATE). Both readings would have a host
        expect the wrong thing of a merge onto "Alex" or "Mom".

        What the write actually does, column by column: email and phone are
        COALESCE(NULLIF(...)) so a blank cell preserves what is stored; segment,
        RSVP and notes are set when the file supplies them; full_name is never
        touched, because the row was matched by an identity the host approved and
        the stored spelling is the agreed one.
      */}
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Attaching fills in the email, phone, RSVP, notes and segment your file supplies —
        blank email and phone cells leave what is already stored alone.
        The guest keeps the name that is already on the roster; attaching never renames them.
        Nothing is written until you confirm below.
      </Alert>

      {summary.pending > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Two people can share a name. Check the guest shown under each row is the person you
          meant before you accept — the concierge cannot tell one "Mom" from another, and a wrong
          match sends that guest's invitation to someone else's address.
        </Alert>
      ) : null}

      {/*
        The bulk control, and why it is behind a checkbox.

        It used to read "Accept the N unambiguous matches" and it accepted
        every name match on the plan — rows the server had explicitly flagged as
        needing a human. "Unambiguous" was a lie told about the riskiest rows in
        the import: one click, above the fold, before the host had scrolled to a
        single guest card. On a roster holding "Alex", "Alex Greeley" and "Alex
        Merriman", a short-form "Alex" in the paste folds unique on both sides and
        merges onto the wrong human silently.

        It is kept rather than deleted because 137 name matches are otherwise 137
        clicks, and a host clicking "Yes, same person" 137 times in a hurry is not
        safer. What the gate changes is that the assertion becomes deliberate,
        scoped to a stated number of rows, and named for what it is.
      */}
      {bulkRows.length > 0 ? (
        <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={700}>
            {plural(bulkRows.length, 'row')} matched on name or phone only
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            The concierge cannot tell one “Mom” from another, and a phone number is a household
            rather than a person. Accepting these is you saying each one is the same human.
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={vouched} onChange={(e) => setVouched(e.target.checked)} />}
            label={
              <Typography variant="caption">
                {bulkVouchSentence(bulkRows.length)}
              </Typography>
            }
          />
          <Box>
            <Button
              size="small"
              color="warning"
              disabled={!vouched}
              onClick={() => {
                onDecisions(acceptAllConfirmable(preview, decisions));
                // Spent: the next plan, or the next batch of pending rows, is a
                // fresh assertion rather than one inherited from a tick the host
                // made about different guests.
                setVouched(false);
              }}
            >
              {bulkAcceptLabel(bulkRows.length)}
            </Button>
          </Box>
        </Box>
      ) : null}

      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={1} sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
        {ordered.map((row) => (
          <PlanRow
            key={row.rowIndex}
            row={row}
            decision={decisions[row.rowIndex]}
            onDecide={decide}
          />
        ))}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={saving || summary.pending > 0} onClick={onConfirm}>
          {saving ? 'Importing...' : `Import ${plural(preview.rows.length, 'row')}`}
        </Button>
        <Button variant="outlined" disabled={saving} onClick={onCancel}>Cancel</Button>
      </Stack>
      {summary.pending > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Resolve the {plural(summary.pending, 'row')} above to enable the import.
        </Typography>
      ) : null}
    </Paper>
  );
}
